import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { motion } from 'framer-motion';
import { MapPin } from 'lucide-react';

const ConversationPathHeatmap = ({ filters, reducedMotion = false }) => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  const [pathData, setPathData] = useState({ cells: [], rows: [], cols: [] });
  const [loading, setLoading] = useState(true);
  const animationDuration = reducedMotion ? 0 : 0.5;

  useEffect(() => {
    fetchPathData();
  }, [filters]);

  const fetchPathData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/analytics/conversation-path`);
      const result = await response.json();
      setPathData(result);
    } catch (error) {
      console.error('Error fetching conversation path:', error);
    } finally {
      setLoading(false);
    }
  };

  const getColorForCount = (count, maxCount) => {
    const intensity = Math.min(count / maxCount, 1);
    
    if (intensity > 0.7) return { bg: '#10b981', text: '#ffffff' }; // Emerald
    if (intensity > 0.4) return { bg: '#22c55e', text: '#ffffff' }; // Green
    if (intensity > 0.2) return { bg: '#84cc16', text: '#000000' }; // Lime
    if (intensity > 0.1) return { bg: '#eab308', text: '#000000' }; // Yellow
    return { bg: '#334155', text: '#94a3b8' }; // Slate
  };

  const maxCount = Math.max(...(pathData.cells?.map(c => c.count) || [1]));

  return (
    <Card className="bg-slate-900/50 border-slate-800">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg text-slate-100">
          <MapPin className="w-5 h-5 text-ink-500" />
          Conversation Path Heatmap
        </CardTitle>
        <p className="text-xs text-slate-400 mt-1">
          Common conversation stage transitions • Color = frequency
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center h-80">
            <div className="animate-pulse text-slate-400">Loading conversation paths...</div>
          </div>
        ) : pathData.cells.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <p>No conversation path data available yet</p>
            <p className="text-xs mt-2">Paths will appear as calls progress through stages</p>
          </div>
        ) : (
          <motion.div
            initial={reducedMotion ? {} : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: animationDuration }}
          >
            <div className="overflow-x-auto">
              <div className="inline-block min-w-full">
                {/* Header row */}
                <div className="flex gap-1 mb-1 ml-32">
                  <div className="w-20 h-8"></div>
                  {pathData.cols.map((col, idx) => (
                    <div key={idx} className="w-20 h-8 flex items-center justify-center">
                      <span className="text-xs text-slate-400 transform -rotate-45 origin-bottom-left">
                        {col}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Heatmap grid */}
                {pathData.rows.map((row, rowIdx) => (
                  <div key={rowIdx} className="flex gap-1 mb-1">
                    {/* Row label */}
                    <div className="w-32 h-12 flex items-center justify-end pr-2">
                      <span className="text-xs text-slate-400 text-right">{row}</span>
                    </div>

                    {/* Cells */}
                    {pathData.cols.map((col, colIdx) => {
                      const cell = pathData.cells.find(c => c.row === row && c.col === col);
                      const colors = cell ? getColorForCount(cell.count, maxCount) : { bg: '#1e293b', text: '#475569' };
                      
                      return (
                        <motion.div
                          key={`${rowIdx}-${colIdx}`}
                          className="w-20 h-12 rounded flex flex-col items-center justify-center cursor-pointer hover:ring-2 hover:ring-gold/40 transition-all"
                          style={{ backgroundColor: colors.bg }}
                          initial={reducedMotion ? {} : { scale: 0.8, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{
                            duration: animationDuration,
                            delay: (rowIdx * pathData.cols.length + colIdx) * 0.01,
                            ease: 'easeOut'
                          }}
                          whileHover={reducedMotion ? {} : { scale: 1.05 }}
                        >
                          {cell && (
                            <>
                              <span className="text-sm font-bold" style={{ color: colors.text }}>
                                {cell.count}
                              </span>
                              {cell.avg_duration_ms && (
                                <span className="text-xs" style={{ color: colors.text, opacity: 0.8 }}>
                                  {Math.round(cell.avg_duration_ms / 1000)}s
                                </span>
                              )}
                            </>
                          )}
                        </motion.div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>

            {/* Legend */}
            <div className="mt-6 flex items-center justify-center gap-2">
              <span className="text-xs text-slate-400">Frequency:</span>
              <div className="flex gap-1">
                {[
                  { color: '#334155', label: 'Rare' },
                  { color: '#eab308', label: 'Low' },
                  { color: '#84cc16', label: 'Medium' },
                  { color: '#22c55e', label: 'High' },
                  { color: '#10b981', label: 'Very High' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-1">
                    <div className="w-4 h-4 rounded" style={{ backgroundColor: item.color }}></div>
                    <span className="text-xs text-slate-400">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Stats */}
            {pathData.meta && (
              <div className="mt-6 grid grid-cols-3 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-ink-500">{pathData.meta.total_transitions || 0}</p>
                  <p className="text-xs text-slate-400 mt-1">Total Paths</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-gold">{pathData.rows.length || 0}</p>
                  <p className="text-xs text-slate-400 mt-1">Stages</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-emerald-400">{pathData.meta.sample_size || 0}</p>
                  <p className="text-xs text-slate-400 mt-1">Calls Analyzed</p>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </CardContent>
    </Card>
  );
};

export default ConversationPathHeatmap;
